import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Flame, Target, Clock, Loader2 } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const DaysOnJourney = ({ roadmaps = [] }) => {
    const [journeyData, setJourneyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            setError(false);
            try {
                const result = await featuresAPI.getJourneyStats();
                if (result && result.days_on_journey != null) {
                    setJourneyData({
                        days: result.days_on_journey || 0,
                        careerPath: result.career_path || (roadmaps.find(r => r.isActive) || roadmaps[0])?.careerPath || 'Your Journey',
                        progress: result.progress || 0,
                        streak: result.current_streak || 0,
                        milestone: _getMilestoneLabel(result.days_on_journey || 0),
                    });
                } else {
                    setError(true);
                }
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [roadmaps]);

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-emerald-500/5 via-transparent to-teal-500/5 border border-emerald-500/10 rounded-xl p-4"
            >
                <div className="flex items-center justify-center gap-2 py-2">
                    <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                    <span className="text-xs text-slate-500">Loading journey stats...</span>
                </div>
            </motion.div>
        );
    }

    if (error || !journeyData) {
        return null; // Silently hide if no data
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-emerald-500/5 via-transparent to-teal-500/5 border border-emerald-500/10 rounded-xl p-4"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-bold text-white">{journeyData.days}</span>
                            <span className="text-xs text-slate-400">days on journey</span>
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono">{journeyData.careerPath}</div>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="text-center">
                        <div className="flex items-center gap-1 text-amber-400">
                            <Flame className="w-3 h-3" />
                            <span className="text-sm font-bold">{journeyData.streak}</span>
                        </div>
                        <div className="text-[9px] text-slate-500 font-mono">STREAK</div>
                    </div>
                    <div className="text-center">
                        <span className="text-sm font-bold text-emerald-400">{journeyData.milestone}</span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

function _getMilestoneLabel(days) {
    if (days >= 365) return '1 Year! 🏆🏆';
    if (days >= 180) return '6 Months! 🏆';
    if (days >= 90) return '90 Days! 🥇';
    if (days >= 60) return '60 Days! 🥈';
    if (days >= 30) return '30 Days! 🥉';
    if (days >= 7) return '1 Week! ⭐';
    return 'Just Started! 🌱';
}

export default DaysOnJourney;
